import { all, call, put, takeEvery } from 'redux-saga/effects';
import { gcApi } from 'config/axios';
import {
    FETCH_QUERY_EVENTS,
    FETCH_QUERY_EVENTS_FETCHING,
    FETCH_QUERY_EVENTS_SUCCESS,
    FETCH_QUERY_EVENTS_FAIL
} from 'actions/eventBasedGoalActions';

import UrlParser from 'utils/parsers/UrlParser';
import NumberParser from 'utils/parsers/NumberParser';
import Conversion from 'utils/Conversion';
import { handlingErrorCallback, handlingSuccessCallback } from './sagasUtils';
import Personalizer from 'utils/Personalizer';

const urlParser = new UrlParser();

const configureForVirtual = (query) => {
    query.includeInPerson = false;
    query.startDistance = null;
    query.endDistance = null;
    query.poiLat = null;
    query.poiLon = null;
    query.withinMeters = null;
};

const configureForInPersion = (query) => {
    query.includeVirtual = false;
};

export function* fetchQueriedEvents(action) {
    const numberParser = new NumberParser({
        numberSeparator: Personalizer.getNumberSeparator()
    });
    let { query, callback } = action;

    yield put({
        type: FETCH_QUERY_EVENTS_FETCHING
    });

    try {
        if (query.eventType === 'all') {
            query.eventType = null; // Will be removed below
        }

        let discoverQuery = {
            eventType: query.eventType,
            searchPhrase: query.searchPhrase,
            poiLat: query.poiLat,
            poiLon: query.poiLon,
            withinMeters: query.filterRadius,
            fromDate: query.fromDate,
            toDate: query.toDate,
            includeInPerson: query.includeInPerson,
            includeVirtual: query.includeVirtual,
            verifiedStatuses: query.verifiedStatuses,
            limit: 200
        };

        if (query.eventsProviders) {
            discoverQuery.eventsProviders = query.eventsProviders;
        }

        if (
            (Number.isFinite(query.startDurationInSeconds) && query.startDurationInSeconds > 0) ||
            (Number.isFinite(query.endDurationInSeconds) && query.endDurationInSeconds > 0)
        ) {
            discoverQuery.completionTargetMinValue = query.startDurationInSeconds;
            discoverQuery.completionTargetMaxValue = query.endDurationInSeconds || 172800; // default to 48 hours
            discoverQuery.completionTargetDurationType = 'time';
        } else {
            if (query.startDistance) {
                discoverQuery.completionTargetMinValue = Math.floor(
                    Conversion.convertDistance(numberParser.parse('' + query.startDistance), query.unit, 'meter')
                );
            }
            if (query.endDistance) {
                discoverQuery.completionTargetMaxValue = Math.ceil(
                    Conversion.convertDistance(numberParser.parse('' + query.endDistance), query.unit, 'meter')
                );
            }
            discoverQuery.completionTargetDurationType = 'distance';
        }

        if (query.filterRadius === 'worldwide' || !query.filterRadius || !query.poiLat || !query.poiLon) {
            discoverQuery.poiLat = null;
            discoverQuery.poiLon = null;
            discoverQuery.withinMeters = null;
        }

        let queryQrray = [discoverQuery];

        if (discoverQuery.includeVirtual && discoverQuery.includeInPerson) {
            let discoverQuery2 = { ...discoverQuery };
            configureForInPersion(discoverQuery);
            configureForVirtual(discoverQuery2);
            // virtual will be last in the results
            queryQrray.push(discoverQuery2);
        } else {
            if (discoverQuery.includeVirtual) {
                configureForVirtual(discoverQuery);
            } else if (discoverQuery.includeInPerson) {
                configureForInPersion(discoverQuery);
            }
        }

        let responses = yield all(
            queryQrray.map((query) => {
                return call(gcApi.get, `/race-search/events${urlParser.convertObjectToQueryString(query)}`);
            })
        );

        let results = [];

        responses.forEach((response) => {
            response.data.forEach((item) => {
                if (item.completionTargets.length > 1) {
                    // Currently we can only handle one completion target. Active.com can contain multiple completion targets
                    // We need to split them into one item per array element.
                    item.completionTargets.forEach((target) => {
                        let copy = { ...item };
                        copy.completionTargets = [target];
                        results.push(copy);
                    });
                } else if (item.completionTargets.length === 0) {
                    // Don't display events which have no completionTarget (empty array).
                } else {
                    results.push(item);
                }
            });
        });

        results.sort((a, b) => {
            return new Date(a.eventDate) - new Date(b.eventDate);
        });

        yield put({
            type: FETCH_QUERY_EVENTS_SUCCESS,
            data: results
        });

        yield call(handlingSuccessCallback, callback, { data: results });
    } catch (error) {
        yield put({
            type: FETCH_QUERY_EVENTS_FAIL
        });
        yield call(handlingErrorCallback, callback, error);
    }
}

export const eventGoals = [takeEvery(FETCH_QUERY_EVENTS, fetchQueriedEvents)];
